// src/pages/login/login-shell.tsx
/** @jsxImportSource @emotion/react */
import type React from 'react';
import { createElement } from 'react';
import { AnimatePresence, type Variants, type Easing, circOut } from 'framer-motion';

import { ClickableLogo, LogoWrapper } from '../registration/shared.styled';
import logoLight from '/main-logo-white.svg';
import logoDark from '/main-logo-white.svg';

import { Wrapper, Container, ShellLogoImg } from '../registration/registration.styled';
import type { LoginShellProps, LoginStepProps } from './login-shell.types';
import { useLoginShell } from './login-shell.hooks';
import { VSpace } from '../chat/settings/account/account-settings';

/* ───────────────── logo ───────────────── */
export const ShellLogo = ({ isLight, small }: { isLight: boolean; small: boolean }) => {
  return (
    <ClickableLogo data-testid="login-logo">
      <LogoWrapper data-testid="login-logo-wrapper">
        <ShellLogoImg
          src={isLight ? logoDark : logoLight}
          alt="Mailander logo"
          small={small}
          data-testid="login-logo-img"
        />
      </LogoWrapper>
    </ClickableLogo>
  );
};

/* ───────────────── animation ───────────────── */
const easeOut: Easing = [0.17, 0.67, 0.83, 0.67];
const variants: Variants = {
  initial: { x: 50, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { duration: 0.5, ease: easeOut } },
  exit: { x: -50, opacity: 0, transition: { duration: 0.4, ease: circOut } },
};

const LoginShellInner: React.FC<LoginShellProps> = ({
  initialStep,
  steps,
  syncToUrl = true,
  urlKey = 's',
}) => {
  const isLight = false;
  const smallLogo = true;

  const { stepId, Step, go, replace, back, data, setData } = useLoginShell({
    initialStep,
    registry: steps,
    syncToUrl,
    urlKey,
  });

  const showLogo = true;

  return (
    <Wrapper bg={'#1A1A1C'} $fill={true} data-testid="login-wrapper" data-step-id={stepId}>
      <VSpace $h={24} />
      {showLogo && <ShellLogo isLight={isLight} small={smallLogo} />}

      <AnimatePresence mode="wait">
        <Container
          key={stepId}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-testid="login-step-container"
          $align={'center'}
          $justify={'flex-start'}
        >
          {createElement<LoginStepProps>(Step, {
            stepId,
            go,
            replace,
            back,
            data,
            setData,
          })}
        </Container>
      </AnimatePresence>
    </Wrapper>
  );
};

export const LoginShell: React.FC<LoginShellProps> = (props) => {
  return <LoginShellInner {...props} />;
};
